import React from "react";
import {
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  Button,
  Stack,
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Switch,
} from "@mui/material";
import FilterIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

import { ACCESS_FILTERS } from "./paperAccess";

/**
 * Search + filters for the tutor Papers / Mock Exams list. Every value is a query parameter on
 * the list endpoint, so filtering happens on the server across all pages.
 */
export default function PaperFilters({
  search,
  onSearchChange,
  filters,
  onChange,
  options,
  groups,
  showCategory = false,
  onReset,
}) {
  const set = (key) => (e) => onChange(key, e.target.value);

  const select = (key, label, items, md = 2) => (
    <Grid item xs={12} sm={6} md={md}>
      <TextField select fullWidth size="small" label={label} value={filters[key]} onChange={set(key)}>
        <MenuItem value="">
          <em>— All —</em>
        </MenuItem>
        {items.map((item) => (
          <MenuItem key={item.id} value={item.id}>
            {item.label ?? item.name}
          </MenuItem>
        ))}
      </TextField>
    </Grid>
  );

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, mb: 2, borderColor: "#eef0f4", boxShadow: "0 4px 12px rgba(0,0,0,0.04)" }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <FilterIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2" fontWeight={700}>
            Search & filter
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button size="small" startIcon={<RefreshIcon />} onClick={onReset}>
            Reset
          </Button>
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} md={showCategory ? 4 : 6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name or ID…"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => onSearchChange("")} aria-label="Clear search">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Grid>
          {select("school_id", "School", options.schools || [])}
          {select("school_year_id", "School year", options.school_years || [])}
          {select("subject_id", "Subject", options.subjects || [])}
          {showCategory && select("category_id", "Category", options.categories || [])}
        </Grid>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "center" }}
          sx={{ mt: 2 }}
        >
          <ToggleButtonGroup
            size="small"
            exclusive
            value={filters.access}
            onChange={(_, v) => v !== null && onChange("access", v)}
            sx={{ flexWrap: "wrap", "& .MuiToggleButton-root": { fontWeight: 600, textTransform: "none", px: 1.5 } }}
          >
            {ACCESS_FILTERS.map((s) => (
              <ToggleButton key={s.value || "all"} value={s.value}>
                {s.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Box sx={{ flex: 1 }} />

          <TextField
            select
            size="small"
            label="Check against group"
            value={filters.course_plan_id}
            onChange={set("course_plan_id")}
            sx={{ minWidth: 240 }}
            helperText={groups.length === 0 ? "You aren't assigned to any group yet" : undefined}
          >
            <MenuItem value="">
              <em>— None —</em>
            </MenuItem>
            {groups.map((g) => (
              <MenuItem key={g.id} value={g.id}>
                {g.name}
                {g.course?.name ? ` — ${g.course.name}` : ""}
              </MenuItem>
            ))}
          </TextField>

          <FormControlLabel
            disabled={!filters.course_plan_id}
            control={
              <Switch
                checked={Boolean(filters.group_assigned)}
                onChange={(e) => onChange("group_assigned", e.target.checked)}
              />
            }
            label="Only assigned to this group"
          />
        </Stack>
      </CardContent>
    </Card>
  );
}
